/** Home diagnostics never use the singleton audio recorder. Only rendered plans belong here. */
import type { Cadence, Manifest, Recording, SessionPlan } from '../types';
import { planFingerprint, protectedTransitions } from './plan';
import { FLOW_REVIEW_SHA256 } from './flow-review';
import { contentDigest } from '../composition/lineage';
import { BANDS, MAX_EVENT_BYTES, MAX_HOME_BYTES, MAX_HOME_EVENTS, jsonBytes, validPreviewChunk } from './preview-schema';
import type { ChoiceReason, PreviewChunk, PreviewEntry, PreviewResolved, Trigger } from './preview-schema';
export const PREVIEW_KEY = 'brahm.home-preview.v1';
export const diagnosticDigest = (v: unknown): string => {
  let n = 2166136261;
  for (const c of JSON.stringify(v)) n = Math.imul(n ^ c.charCodeAt(0), 16777619);
  return `fnv1a:${(n >>> 0).toString(16).padStart(8, '0')}`;
};
export interface PreviewInput {
  manifest: Manifest; recording: Recording | null; plan: SessionPlan | null; target: number;
  cadence: Cadence; intentKind: 'duration' | 'deadline'; trigger: Trigger; reason: ChoiceReason;
  previousRecordingId: string | null; picked: boolean; historyRevision: number;
  pool: { poolSize: number; freshPoolSize: number; recencyRelaxed: boolean };
  plannerVersion: string; planMs: number; problem?: string | null; desiredSpeechRatio?: number;
}
export function previewPayload(x: PreviewInput): Omit<PreviewResolved, 'homeSessionId' | 'seq' | 'actionId' | 'elapsedMs'> {
  const p = x.plan, r = x.recording;
  const fp = p && r ? planFingerprint(p, r) : null;
  let blocks = 0, inSpeech = false;
  for (const s of p?.timeline ?? []) {
    if (s.kind === 'speech' && !inSpeech) blocks++;
    inSpeech = s.kind === 'speech';
  }
  return {
    schema: 1, trigger: x.trigger, intent: {kind: x.intentKind, targetSec: Math.max(0,x.target)},
    toleranceSec: x.intentKind === 'deadline' ? 0 : 30, cadence: x.cadence, picked: x.picked,
    recordingId: r?.id ?? null, previousRecordingId: x.previousRecordingId, reason: x.reason,
    releaseDigest: x.manifest.releaseDigest ?? 'unavailable', manifestDigest: x.manifest.manifestDigest ?? 'unavailable',
    plannerVersion: x.plannerVersion, policyVersion: p?.composition?.policyVersion ?? 'legacy-cadence-v1', historyRevision: x.historyRevision,
    eligibility: {status: p ? 'conforming' : 'unavailable', reason: p ? null : (x.problem ?? 'unavailable'),
      poolSize:x.pool.poolSize,freshPoolSize:x.pool.freshPoolSize,recencyRelaxed:x.pool.recencyRelaxed,shapeDeficits: p?.composition?.shape.deficits ?? []}, band: BANDS[x.cadence],
    spokenSec: p?.preview.spokenSec ?? null, actualSec: p?.actualSec ?? null,
    share: p ? p.preview.spokenSec / p.actualSec : null,
    speechBlocks: p ? blocks : null, sections: p ? p.composition?.sections.length ?? blocks : null, middleSections: p?.preview.reminderCount ?? null,
    longestSilenceSec: p?.preview.longestSilenceSec ?? null,
    displayed: { spokenMinutes: p ? Math.round(p.preview.spokenSec / 60) : null,
      middleSections: p?.preview.reminderCount ?? null,
      longestSilenceMinutes: p ? Math.round(p.preview.longestSilenceSec / 60) : null },
    planFingerprint: fp, fingerprintDigest: fp ? diagnosticDigest(fp) : null,
    annotation: { flowReview: FLOW_REVIEW_SHA256,
      variantIds: p?.composition?.variants.map(v=>v.id) ?? [p?.preview.speechRatioAdjustment?.openingVariantId, p?.preview.speechRatioAdjustment?.closingVariantId].filter((id): id is string => !!id),
      transitionIds: r ? [...protectedTransitions(r)].map(i => `after-${i}`) : [] },
    ...(p?.composition ? {composition:{variants:p.composition.variants,wordlessIslands:p.composition.wordlessIslands,shapeScore:p.composition.shape.score}} : {}),
    desiredSpeechRatio: x.desiredSpeechRatio ?? null, planMs: x.planMs, cache: 'miss',
  };
}
export function startPayload(preview: PreviewResolved, plan: SessionPlan, recording: Recording) {
  const fp = planFingerprint(plan, recording);
  const differs = JSON.stringify(fp) !== JSON.stringify(preview.planFingerprint) || recording.id !== preview.recordingId;
  const speechDiffers = recording.id !== preview.recordingId || JSON.stringify(fp.speechSpans) !== JSON.stringify(preview.planFingerprint?.speechSpans);
  return { schema: 1 as const, fromHomeSessionId: preview.homeSessionId, previewSeq: preview.seq,
    previewTargetSec: preview.intent.targetSec, startTargetSec: plan.targetSec, recordingId: recording.id,
    planFingerprint: fp, differs, speechDiffers,
    deadlineSilenceOnly: differs && !speechDiffers && preview.intent.kind === 'deadline' };
}
interface Visit { id: string; part: number; nextSeq: number; action: number; origin: number;
  open: PreviewChunk; chunks: PreviewChunk[]; droppedByKind: Record<string, number> }
function storage(): Storage | null { try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; } }
function newChunk(id: string, part: number, droppedByKind: Record<string, number>): PreviewChunk {
  return { id: `home-${id}-part-${part}`, startedAt: Math.floor(Date.now()/86400000)*86400000,
    header: {kind:'home-preview',schema:1,homeSessionId:id,part,firstSeq:0,lastSeq:0,droppedByKind:{...droppedByKind}},
    entries:[], dropped:Object.values(droppedByKind).reduce((a,b)=>a+b,0), anomalies:{} };
}
export function createPreviewTrace(deps: { storage?: () => Storage | null; now?: () => number; id?: () => string } = {}) {
  const store = deps.storage ?? storage, now = deps.now ?? (() => performance.now());
  let visits: Visit[] = [], active: Visit | null = null, last: PreviewResolved | null = null;
  let ticks: {first: number; last: number; count: number; previewSeq: number} | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => { clearTimeout(timer); timer = undefined;
    try {
      // A durable snapshot includes the pending interval without turning every tick into a row.
      const snapshot = structuredClone(visits);
      const v = active && snapshot.find(v => v.id === active?.id);
      if (v && ticks) {
        const seq = v.nextSeq++;
        v.open.entries.push({seq,kind:'deadline_interval',detail:{schema:1,homeSessionId:v.id,seq,actionId:v.action,
          elapsedMs:ticks.last,firstElapsedMs:ticks.first,lastElapsedMs:ticks.last,count:ticks.count,previewSeq:ticks.previewSeq}});
        v.open.header.firstSeq=v.open.entries[0].seq;v.open.header.lastSeq=seq;
      }
      if (v) {
        while (v.chunks.length && (v.open.entries.length+v.chunks.reduce((n,c)=>n+c.entries.length,0)>MAX_HOME_EVENTS || jsonBytes(v)>MAX_HOME_BYTES-1024)) {
          for (const e of v.chunks.shift()!.entries) v.droppedByKind[e.kind]=(v.droppedByKind[e.kind]??0)+1;
        }
        v.open.header.droppedByKind={...v.droppedByKind};
        v.open.dropped=Object.values(v.droppedByKind).reduce((a,b)=>a+b,0);
      }
      store()?.setItem(PREVIEW_KEY, JSON.stringify(snapshot));
    } catch { /* Quota/private mode cannot affect a sit. */ } };
  const persist = (immediate: boolean) => { if (immediate) flush(); else if (!timer) timer = setTimeout(flush, 500); };
  try {
    const saved: unknown = JSON.parse(store()?.getItem(PREVIEW_KEY) ?? '[]');
    if (Array.isArray(saved)) visits = saved.slice(-3).filter(v => v && typeof v.id === 'string' && Array.isArray(v.chunks)
      && typeof v.nextSeq === 'number' && v.open && jsonBytes(v) <= MAX_HOME_BYTES)
      .map(v => ({...v, chunks: [...v.chunks, ...(v.open.entries?.length ? [v.open] : [])].filter(validPreviewChunk),
        part: v.part + 1, open: newChunk(v.id, v.part + 1, v.droppedByKind ?? {})}));
  } catch { /* Missing or damaged home evidence means unknown coverage, never an audio error. */ }
  const ensure = () => {
    if (!active) {
      const id = deps.id?.() ?? crypto.randomUUID();
      active = {id,part:0,nextSeq:0,action:0,origin:now(),open:newChunk(id,0,{}),chunks:[],droppedByKind:{}};
      visits.push(active); visits = visits.slice(-3); last = null;
    }
    return active;
  };
  const sealOpen = () => {
    if (!active || !active.open.entries.length) return;
    active.chunks.push(structuredClone(active.open));
    active.part++; active.open = newChunk(active.id, active.part, active.droppedByKind);
  };
  const bound = (v: Visit) => {
    const count = () => v.open.entries.length + v.chunks.reduce((n,c)=>n+c.entries.length,0);
    while (v.chunks.length && (count() > MAX_HOME_EVENTS || jsonBytes(v) > MAX_HOME_BYTES-1024)) {
      const removed = v.chunks.shift()!;
      for (const e of removed.entries) v.droppedByKind[e.kind] = (v.droppedByKind[e.kind] ?? 0) + 1;
    }
    v.open.header.droppedByKind = {...v.droppedByKind};
    v.open.dropped = Object.values(v.droppedByKind).reduce((a,b)=>a+b,0);
  };
  const append = (kind: PreviewEntry['kind'], payload: object, immediate = true): PreviewEntry => {
    const v = ensure(), seq = v.nextSeq++;
    const detail = {...payload, homeSessionId:v.id,seq,actionId:v.action,elapsedMs:Math.max(0,now()-v.origin)};
    let entry = {seq,kind,detail} as PreviewEntry;
    if (jsonBytes(entry) > MAX_EVENT_BYTES) {
      v.droppedByKind[kind] = (v.droppedByKind[kind] ?? 0) + 1;
      entry = {seq,kind:'preview_omitted',detail:{schema:1,homeSessionId:v.id,seq,actionId:v.action,
        elapsedMs:detail.elapsedMs,omittedKind:kind,bytes:jsonBytes(entry),digest:diagnosticDigest(entry)}};
    }
    if (v.open.entries.length >= MAX_HOME_EVENTS || jsonBytes(v.open)+jsonBytes(entry) > MAX_HOME_BYTES-2048) sealOpen();
    v.open.entries.push(entry); v.open.header.firstSeq = v.open.entries[0].seq; v.open.header.lastSeq = seq;
    bound(v);
    if (v.open.entries.length >= MAX_HOME_EVENTS || jsonBytes(v.open) >= MAX_HOME_BYTES-6144) sealOpen();
    persist(immediate); return entry;
  };
  const flushTicks = () => {
    if (!ticks) return;
    append('deadline_interval',{schema:1,firstElapsedMs:ticks.first,lastElapsedMs:ticks.last,count:ticks.count,previewSeq:ticks.previewSeq}, false);
    ticks = null;
  };
  const seal = () => { flushTicks(); sealOpen(); flush(); };
  return {
    record(payload: ReturnType<typeof previewPayload>): PreviewResolved | null {
      const v = ensure(); ++v.action;
      if (payload.trigger === 'deadline-tick' && last && JSON.stringify(payload.planFingerprint) === JSON.stringify(last.planFingerprint)
          && payload.recordingId === last.recordingId && payload.eligibility.reason === last.eligibility.reason) {
        const elapsed = Math.max(0,now()-v.origin);
        ticks ??= {first:elapsed,last:elapsed,count:0,previewSeq:last.seq}; ticks.last = elapsed; ticks.count++;
        // Persist the interval rather than materialising every unchanged second.
        persist(false); return last;
      }
      flushTicks();
      const e = append('preview_resolved',payload,payload.trigger !== 'deadline-tick' || !payload.planFingerprint);
      // Even an omitted oversized preview keeps its sequence available for the Sit link.
      last = {...payload,homeSessionId:e.detail.homeSessionId,seq:e.seq,actionId:e.detail.actionId,elapsedMs:e.detail.elapsedMs};
      return last;
    },
    start(plan: SessionPlan, recording: Recording, preview = last) {
      if (preview) { flushTicks(); append('sit_start_resolved',startPayload(preview,plan,recording)); }
      seal();
    },
    seal,
    leave() { seal(); active = null; last = null; },
    flush() { flushTicks(); flush(); },
    sealed: (): PreviewChunk[] => structuredClone(visits.flatMap(v => v.chunks)),
    clear() { clearTimeout(timer); timer=undefined; visits=[];active=null;last=null;ticks=null;try{store()?.removeItem(PREVIEW_KEY);}catch{ /* best effort */ } },
  };
}
export const previewTrace = createPreviewTrace();
/** Content identity is calculated only over a sealed chunk, before it enters upload dedup. */
export async function previewEnvelope(chunk: PreviewChunk) {
  if (!validPreviewChunk(chunk)) throw new Error('invalid home-preview chunk');
  const digest = await contentDigest(chunk);
  return {exportedAt:new Date(chunk.startedAt).toISOString(),userAgent:null,
    sessions:[{...chunk,id:`${chunk.id}-${digest.slice(7)}`}]};
}
